import { flatten } from "ramda";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

import type { Contig, CoordinateList, InputRestriction, Organism, ProjectFile, Range } from "@/graphql.ts";
import {
  type IMutantGenome,
  type ISortableTrack,
  createMutantGenomesTableQuerySpec,
  getAllTableSpecsFromTrackUsingGenomeInformation,
  getDisplayedTracksWithSelectableFeatures,
  getFeatureTableRestrictions,
  highlightGenomeFeature,
  highlightMutantGenome,
  zoomToRegion,
} from "@/redux/modules/genome-browser.ts";
import type { IIdentifiable } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, FEATURE_DISPLAY_NAME, TableRestriction } from "@/settings/settings.ts";
import type { ITabbedTableCollection } from "@/ui-modules/genome-viewer/genomic-table/GenomeViewerTabbedTableView.tsx";
import { MUTANT_GENOMES_COLLECTION_NAME } from "@/ui-modules/genome-viewer/genomic-table/GenomicFeatureTable.tsx";

/*
  based on the list of visible tracks, we infer the list of grids
 */
export const useGenomicTableCollections = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome);
  const genomeIsLoading = useSelector<IAppState, boolean>((state) => state.genomeBrowser.isLoading);
  const tracks = useSelector<IAppState, ISortableTrack[]>((state) => state.genomeBrowser.tracks);
  const showFlags = useSelector<IAppState, boolean>((state) => state.auth.session?.params.showFlagsTrack);
  const selectedMutantGenomes = useSelector<IAppState, IMutantGenome[]>(
    (state) => state.genomeBrowser.selectedMutantGenomes,
  );

  const bedDatafiles = useSelector<IAppState, Record<number, ProjectFile>>((state) => state.genomeBrowser.bedDatafiles);
  const coordinateLists = useSelector<IAppState, Record<number, CoordinateList>>(
    (state) => state.genomeBrowser.coordinateLists,
  );

  const collections = useMemo(() => {
    if (!genome || genomeIsLoading) {
      return [];
    }
    // get list of tracks that will have one or more corresponding tables
    const tableTracks = getDisplayedTracksWithSelectableFeatures(tracks, showFlags);
    // map the tracks to one or more tables
    const trackCollections: ITabbedTableCollection[] = flatten(
      tableTracks.map((track) => {
        return dispatch(getAllTableSpecsFromTrackUsingGenomeInformation(track))?.map((tableSpec) => {
          let subTitle = null;
          if (tableSpec.resultType === ENTITY_TYPE.BED_FEATURE) {
            subTitle = bedDatafiles[tableSpec.id]?.fileName;
          } else if (tableSpec.resultType === ENTITY_TYPE.COORDINATE) {
            subTitle = coordinateLists[tableSpec.id]?.name;
          } else if (tableSpec.resultSubTypeId) {
            subTitle = FEATURE_DISPLAY_NAME[tableSpec.resultType];
          }

          return {
            id: genome.id,
            name: track?.name,
            type: subTitle,
            tableSpec: tableSpec,
            hideCheckboxes: tableSpec.resultType === ENTITY_TYPE.REGION_FLAG,
            enableTableRestrictions: true,
            onFeatureFocused: (rowData: any) => (rowData?.region ? dispatch(zoomToRegion(rowData.region)) : null),
            onCellMouseOver: (...args) => dispatch(highlightGenomeFeature(...args)),
            onCellMouseOut: () => dispatch(highlightGenomeFeature(null, null)),
          } satisfies ITabbedTableCollection;
        });
      }),
    );

    // special case for the listing of mutant genomes derived from the variants/vcf variants track (if any visible)
    const variantTableSpec = trackCollections.find(
      (collection) =>
        collection.tableSpec.resultType === ENTITY_TYPE.VARIANT ||
        collection.tableSpec.resultType === ENTITY_TYPE.VCF_VARIANT,
    )?.tableSpec;
    if (variantTableSpec) {
      const mutantGenomesTableSpec = createMutantGenomesTableQuerySpec(variantTableSpec, genome, selectedMutantGenomes);
      if (mutantGenomesTableSpec) {
        // e.g. no mutant genome selected
        const mutantGenomesCollection: ITabbedTableCollection = {
          id: genome.id,
          name: `${
            variantTableSpec.resultType === ENTITY_TYPE.VCF_VARIANT ? "" : "Derived "
          }${MUTANT_GENOMES_COLLECTION_NAME}`,
          tableSpec: mutantGenomesTableSpec,
          onFeatureFocused: (rowData: IIdentifiable) => dispatch(highlightMutantGenome(rowData.id as number)),
          enableTableRestrictions: false,
        };
        // move the mutant genomes grid in the position just before the variants grid (so that the variants grid open by default if conditions are there)
        const variantsCollectionPosition = trackCollections.findIndex(
          (collection) =>
            collection.tableSpec.resultType === ENTITY_TYPE.VARIANT ||
            collection.tableSpec.resultType === ENTITY_TYPE.VCF_VARIANT,
        );
        trackCollections.splice(variantsCollectionPosition, 0, mutantGenomesCollection);
      }
    }

    return trackCollections;
  }, [genome, genomeIsLoading, tracks, selectedMutantGenomes, bedDatafiles, coordinateLists, showFlags, dispatch]);

  return collections;
};

export const useGenomicTableRestrictions = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const tableRowRestriction = useSelector<IAppState, TableRestriction>((state) => state.tableData.rowRestriction);
  const selectedMutantGenomes = useSelector<IAppState, IMutantGenome[]>(
    (state) => state.genomeBrowser.selectedMutantGenomes,
  );
  const range = useSelector<IAppState, Range>((state) => state.genomeBrowser.range);
  const contig = useSelector<IAppState, Contig>((state) => state.genomeBrowser.contig);

  // useful to check if the range was already set in order to know if we should debounce the update after range modification
  const [previousRange, setPreviousRange] = useState<Range>(range);
  // restrictions in regards to the genome browser
  const [tableRestrictions, setTableRestrictions] = useState<InputRestriction>(dispatch(getFeatureTableRestrictions()));

  const updateTableRestrictionsState = useCallback(() => {
    const tableRestrictions = dispatch(getFeatureTableRestrictions());
    setTableRestrictions(tableRestrictions);
  }, []);

  const debouncedUpdateTableRestrictionsState = useDebouncedCallback(() => {
    updateTableRestrictionsState();
  }, 300);

  useEffect(() => {
    updateTableRestrictionsState();
  }, [tableRowRestriction, selectedMutantGenomes]);

  useEffect(() => {
    if (tableRowRestriction === TableRestriction.CONTIG) {
      updateTableRestrictionsState();
    }
  }, [contig]);

  useEffect(() => {
    if (tableRowRestriction === TableRestriction.REGION) {
      if (!previousRange) {
        // immediately update e.g. loaded through pagination
        updateTableRestrictionsState();
      } else {
        debouncedUpdateTableRestrictionsState();
      }
    }
    setPreviousRange(range);
  }, [range]);

  return tableRestrictions;
};
